import { useState, useEffect, useRef } from "react";

export interface UseDSTagListProps {
  tags: string[];
  allTags: string[];
  onSave: (tags: string[]) => Promise<void>;
  isLoading?: boolean;
}

export const useDSTagList = ({
  tags = [],
  onSave,
  isLoading: externalLoading = false,
}: UseDSTagListProps) => {
  const [showModal, setShowModal] = useState(false);
  const [currentTags, setCurrentTags] = useState<string[]>(tags);
  const [searchText, setSearchText] = useState("");
  const [copied, setCopied] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setCurrentTags(tags);
  }, [tags]);

  const addTag = (tagText: string) => {
    if (!tagText || !tagText.trim()) return;

    const newTags = tagText
      .split(",")
      .map((t) => t.trim())
      .filter((t) => t !== "" && !currentTags.includes(t));

    if (newTags.length === 0) return;

    setCurrentTags([...currentTags, ...newTags]);
    setSearchText("");

    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const removeTag = (tagToRemove: string) => {
    setCurrentTags(currentTags.filter((tag) => tag !== tagToRemove));
  };

  const handleCopyTags = () => {
    if (!navigator.clipboard) return;

    const tagsString = currentTags.join(",");

    navigator.clipboard
      .writeText(tagsString)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(console.error);
  };

  const handleSave = () => {
    onSave(currentTags).then(() => {
      setShowModal(false);
    });
  };

  const handleCancel = () => {
    setCurrentTags(tags);
    setShowModal(false);
  };

  return {
    showModal,
    currentTags,
    searchText,
    copied,
    isLoading: externalLoading,
    inputRef,
    setShowModal,
    setSearchText,
    addTag,
    removeTag,
    handleCopyTags,
    handleSave,
    handleCancel,
  };
};
